import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LocationService } from '../services/location.service';
import { AnalyticsStatData } from '../shared/types';
import { getAuthHeadersFromUserData } from '../shared/utils/auth-headers.util';

export interface AnalyticsData {
  stats: AnalyticsStatData[];
  chartData: any;
  period: string;
}

export interface RevenueStatisticsApiResponse {
  status: boolean;
  status_code: number;
  message: string;
  data: {
    is_revenue_generated: boolean;
    revenue: {
      value: number;
      growth: number;
    };
    services: {
      value: number;
      growth: number;
    };
    avg_revenue_per_wash: {
      value: number;
      growth: number;
    };
    new_customers: {
      value: number;
      growth: number;
    };
  };
}

/**
 * Maps a period label to its "vs previous period" comparison text.
 * Shared by AnalyticsService and AnalyticsComponent.
 */
export function getPeriodComparisonText(period: string): string {
  const periodMap: Record<string, string> = {
    'Today': 'vs last day',
    'Yesterday': 'vs day before',
    'This Week': 'vs last week',
    'Last Week': 'vs week before',
    'This Month': 'vs last month',
    'Last Month': 'vs month before',
    'This Year': 'vs last year',
    'Last Year': 'vs year before',
    'Custom': ''
  };
  return periodMap[period] || '';
}

/**
* Service that centralizes analytics-related HTTP requests, response mapping and parameter building for dashboard charts and statistics.
* @component
* @selector app-analytics-service
* @example
*   // Inject into a component or another service
*   constructor(private analyticsService: AnalyticsService) {}
* @input none - This injectable does not accept Angular input properties.
* @output none - This injectable does not emit Angular output events; methods return Observables with API data.
*
* Purpose:
*   Provides a single place to fetch revenue, trends, customer and employee analytics from the backend, map API responses into UI-friendly statistics, and build common API parameter sets (including custom date ranges and timezone).
*
* Key methods:
*   - getAnalyticsData(period: string, locationId?: string): Observable<{stats: AnalyticsStatData[]; chartData: any; period: string}>
*       Fetches revenue statistics, maps the API response to presentation-friendly stats and returns a structure containing stats, (placeholder) chartData and the display period. Handles errors by returning empty stats.
*   - getRevenueStatistics(period: string, locationId?: string): Observable<RevenueStatisticsApiResponse>
*       Calls the /revenue-statistics endpoint with proper auth headers and constructed params. Throws errors to be handled by callers.
*   - getRevenueTrendData(filterBy: string, locationId?: string, customParams?): Observable<any>
*       getRevenueByServiceTypeData(...), getServiceTrendsData(...), getCustomerGrowthData(...), getEmployeePerformanceData(...), getEmployeeHoursData(...), getWashCountByServiceTypeData(...), getCustomerRetentionData(...), getCustomerHappinessData(...), getCustomerBreakdownData(...)
*       Each method builds common params and requests the corresponding analytics endpoint, returning Observables and gracefully handling errors (typically returning null).
*   - getAnalyticsStats(period: string, locationId?: string): Observable<AnalyticsStatData[]>
*       Returns only the mapped analytics stat items (used to populate stat cards).
*
* Private helpers:
*   - getAuthHeaders(): builds HttpHeaders including auth and JSON content type.
*   - mapApiResponseToStats(apiData, period): transforms raw API response into an array of AnalyticsStatData with formatted values, percentage strings and period text.
*   - mapPeriodToApiFormat(period): maps human readable period labels to API filter values.
*   - parsePeriodParameter(period): parses period strings including "Custom:start:end" and returns filterBy, optional start/end, and displayPeriod.
*   - buildAnalyticsParams(filterBy, locationId?, customParams?): constructs query params consistently for all analytics endpoints (includes timezone and optional custom dates).
*
* Error handling:
*   - Methods generally catch HTTP errors, log to console, and either rethrow (for callers to handle) or return safe defaults (empty arrays or null) where appropriate to prevent UI breaks.
*
* Lifecycle hooks:
*   - None — this is an injectable service without Angular component lifecycle hooks. Initialization is handled on demand via method calls.
*/
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly apiRoot = `${environment.baseUrl}/${environment.apiVersion}`;
  private readonly apiUrl = `${environment.baseUrl}/${environment.apiVersion}/analytics`;

  constructor(
    private http: HttpClient,
    private locationService: LocationService
  ) {}

  private getAuthHeaders(): HttpHeaders {
    return getAuthHeadersFromUserData({ includeJsonContentType: true });
  }

  /**
   * Get analytics data for the specified period
   * @param period - The time period for analytics data
   * @param locationId - The location ID (optional)
   * @returns Observable<AnalyticsData>
   */
  getAnalyticsData(period: string, locationId?: string): Observable<AnalyticsData> {
    const periodInfo = this.parsePeriodParameter(period);

    return this.getRevenueStatistics(period, locationId).pipe(
      map(response => {
        const stats = this.mapApiResponseToStats(response.data, periodInfo.displayPeriod);
        const chartData = null; // Chart data will be handled by component
        
        return {
          stats: stats,
          chartData: chartData,
          period: periodInfo.displayPeriod
        };
      }),
      catchError(error => {
        console.error('Error fetching analytics data:', error);
        // Return empty stats on error
        return of({
          stats: [],
          chartData: null,
          period: periodInfo.displayPeriod
        });
      })
    );
  }

  /**
   * Get revenue statistics from API
   * @param period - The time period
   * @param locationId - The location ID (optional)
   * @returns Observable<RevenueStatisticsApiResponse>
   */
  getRevenueStatistics(period: string, locationId?: string): Observable<RevenueStatisticsApiResponse> {
    const periodInfo = this.parsePeriodParameter(period);
    const params: any = {
      filter_by: periodInfo.filterBy
    };
    const timezone = this.locationService.getTimezoneForLocationId(locationId);
    if (timezone) {
      params.timezone = timezone;
    }

    if (locationId) {
      params.location_id = locationId;
    }

    if (periodInfo.filterBy === 'custom') {
      if (periodInfo.startDate) {
        params.start_date = periodInfo.startDate;
      }
      if (periodInfo.endDate) {
        params.end_date = periodInfo.endDate;
      }
    }

    return this.http.get<RevenueStatisticsApiResponse>(`${this.apiUrl}/revenue-statistics`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching revenue statistics:', error);
        throw error;
      })
    );
  }

  /**
   * Get revenue trend data for charts
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getRevenueTrendData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiRoot}/revenue/get-revenue-trend`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching revenue trend data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get revenue by service type data for charts
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getRevenueByServiceTypeData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiRoot}/revenue/get-revenue-by-service-type`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching revenue by service type data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get service trends data for charts
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getServiceTrendsData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiUrl}/service-trend`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching service trends data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get customer growth data for charts
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getCustomerGrowthData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiUrl}/customer-growth`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching customer growth data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get employee performance data for charts
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getEmployeePerformanceData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiUrl}/employee-performance`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching employee performance data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get employee hours data for charts
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getEmployeeHoursData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiUrl}/employee-hours`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching employee hours data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get wash count by service type data for popular services chart
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getWashCountByServiceTypeData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiUrl}/wash-count-by-service-type`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching wash count by service type data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get customer retention data for charts
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getCustomerRetentionData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiUrl}/customer-retention`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching customer retention data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get customer happiness data for charts
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getCustomerHappinessData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiUrl}/customer-happiness`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching customer happiness data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get customer breakdown data for charts
   * @param filterBy - The filter type (year, month, week, etc.)
   * @param locationId - The location ID (optional)
   * @returns Observable<any>
   */
  getCustomerBreakdownData(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): Observable<any> {
    const params = this.buildAnalyticsParams(filterBy, locationId, customParams);

    return this.http.get<any>(`${this.apiUrl}/customer-breakdown`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      catchError(error => {
        console.error('Error fetching customer breakdown data:', error);
        return of(null);
      })
    );
  }

  /**
   * Get analytics statistics
   * @param period - The time period for statistics
   * @param locationId - The location ID (optional)
   * @returns Observable<AnalyticsStatData[]>
   */
  getAnalyticsStats(period: string, locationId?: string): Observable<AnalyticsStatData[]> {
    const periodInfo = this.parsePeriodParameter(period);

    return this.getRevenueStatistics(period, locationId).pipe(
      map(response => this.mapApiResponseToStats(response.data, periodInfo.displayPeriod)),
      catchError(error => {
        console.error('Error fetching analytics stats:', error);
        // Return empty array instead of mock data
        return of([]);
      })
    );
  }

  /**
   * Get dynamic period text based on selected period
   * @param period - The selected period
   * @returns string - The appropriate "last period" text
   */
  private getLastPeriodText(period: string): string {
    return getPeriodComparisonText(period);
  }

  /**
   * Map API response to analytics stats format
   * @param apiData - The API response data
   * @param period - The selected period for dynamic period text
   * @returns AnalyticsStatData[]
   */
  private mapApiResponseToStats(apiData: RevenueStatisticsApiResponse['data'], period: string): AnalyticsStatData[] {
    return [
      {
        title: 'Total Revenue',
        value: apiData.revenue.value === 0 ? '-' : `$${apiData.revenue.value.toLocaleString()}`,
        percentage: apiData.revenue.growth > 0 ? `+${apiData.revenue.growth}%` : `${apiData.revenue.growth}%`,
        periodText: this.getLastPeriodText(period),
        gradient: 'linear-gradient(180deg, #A9DF85 0%, #6DA548 100%)'
      },
      {
        title: 'Total Services',
        value: apiData.services.value === 0 ? '-' : apiData.services.value.toLocaleString(),
        percentage: apiData.services.growth > 0 ? `+${apiData.services.growth}%` : `${apiData.services.growth}%`,
        periodText: this.getLastPeriodText(period),
        gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)'
      },
      {
        title: 'Avg. Revenue/Wash',
        value: apiData.avg_revenue_per_wash.value === 0 ? '-' : `$${apiData.avg_revenue_per_wash.value.toLocaleString()}`,
        percentage: apiData.avg_revenue_per_wash.growth > 0 ? `+${apiData.avg_revenue_per_wash.growth}%` : `${apiData.avg_revenue_per_wash.growth}%`,
        periodText: this.getLastPeriodText(period),
        gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)'
      },
      {
        title: 'New Customers',
        value: apiData.new_customers.value === 0 ? '-' : apiData.new_customers.value.toLocaleString(),
        percentage: apiData.new_customers.growth > 0 ? `+${apiData.new_customers.growth}%` : `${apiData.new_customers.growth}%`,
        periodText: this.getLastPeriodText(period),
        gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)'
      }
    ];
  }

  /**
   * Map period to API format
   * @param period - The period string
   * @returns string - API formatted period
   */
  mapPeriodToApiFormat(period: string): string {
    const periodMap: Record<string, string> = {
      'Today': 'today',
      'Yesterday': 'yesterday',
      'This Week': 'week',
      'Last Week': 'last_week',
      'This Month': 'month',
      'Last Month': 'last_month',
      'This Year': 'year',
      'Last Year': 'last_year',
      'Custom': 'custom'
    };
    
    return periodMap[period] || 'today';
  }

  /**
   * Parse period parameter that may include custom date range
   */
  private parsePeriodParameter(period: string): { filterBy: string; startDate?: string; endDate?: string; displayPeriod: string } {
    const sanitizedPeriod = (period || 'Today').trim();

    if (sanitizedPeriod.startsWith('Custom')) {
      const [, startDate = '', endDate = ''] = sanitizedPeriod.split(':');
      return {
        filterBy: 'custom',
        startDate: startDate || undefined,
        endDate: endDate || startDate || undefined,
        displayPeriod: 'Custom'
      };
    }

    return {
      filterBy: this.mapPeriodToApiFormat(sanitizedPeriod),
      displayPeriod: sanitizedPeriod
    };
  }

  /**
   * Build common analytics params with optional custom date range
   */
  private buildAnalyticsParams(filterBy: string, locationId?: string, customParams?: { startDate?: string; endDate?: string }): any {
    const params: any = {
      filter_by: filterBy
    };
    const timezone = this.locationService.getTimezoneForLocationId(locationId);
    if (timezone) {
      params.timezone = timezone;
    }

    if (locationId) {
      params.location_id = locationId;
    }

    if (filterBy === 'custom') {
      if (customParams?.startDate) {
        params.start_date = customParams.startDate;
      }
      if (customParams?.endDate) {
        params.end_date = customParams.endDate;
      }
    }

    return params;
  }


}
